// Sites Module Tour
// Funnels, websites, forms, and landing pages

export const sitesTour = {
  id: 'sites',
  name: 'Sites Module Tour',
  displayName: 'Build High-Converting Funnels & Forms',
  description: 'Create landing pages, funnels, and forms that capture leads',
  estimatedDuration: '6 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux'],
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Welcome to Sites
    {
      element: 'nav #sb_sites',
      popover: {
        title: '🌐 Welcome to Sites',
        description: `
          Build landing pages, funnels, forms, and full websites—no coding required! 
          Whether you need a simple opt-in page or a complete multi-step sales funnel, 
          you can create it here with drag-and-drop ease.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: Sites vs Funnels vs Forms
    {
      popover: {
        title: '🤔 What\'s the Difference?',
        description: `
          **Funnels**: Multi-page sequences designed to convert (landing page → opt-in → thank you)
          
          **Websites**: Full multi-page sites with navigation (Home, About, Services, Contact)
          
          **Forms**: Standalone lead capture forms you can embed anywhere
          
          **Surveys**: Multi-question forms for feedback or qualification
          
          All use the same builder—just different structures and purposes!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 3: Template Library
    {
      element: 'button:contains("Templates"), [href*="template"]',
      popover: {
        title: '📚 Start with Templates',
        description: `
          Don't start from scratch! Browse hundreds of professionally-designed templates:
          • **By industry**: Real estate, coaching, e-commerce, services
          • **By goal**: Lead gen, sales, webinar registration, book a call
          • **Pre-optimized**: Built based on what converts
          
          Pick a template, customize colors/text, and publish in minutes.
        `,
        side: 'left',
        align: 'start',
        proTip: 'Filter templates by your industry for designs proven to work in your niche!'
      }
    },

    // Step 4: Create New Funnel
    {
      element: 'button:contains("New Funnel"), button:contains("Create Funnel")',
      popover: {
        title: '🚀 Create Your First Funnel',
        description: `
          Starting a new funnel:
          1. Click "New Funnel"
          2. Choose: From Template, Blank, or AI-Generated
          3. Name your funnel
          4. Add pages (landing page, opt-in, thank you, etc.)
          5. Customize in the builder
          
          Templates are fastest—blank is for advanced users who want complete control.
        `,
        side: 'left',
        align: 'start'
      }
    },

    // Step 5: Funnel Builder
    {
      popover: {
        title: '🎨 Drag-and-Drop Builder',
        description: `
          The funnel builder is intuitive and powerful:
          • **Drag elements**: Headlines, text, images, buttons, videos
          • **Pre-made sections**: Hero, features, testimonials, FAQ
          • **Mobile preview**: See how it looks on phones (50%+ traffic!)
          • **Undo/redo**: Experiment fearlessly
          • **Global styles**: Set colors/fonts once, apply everywhere
          
          If you can use PowerPoint, you can build funnels!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 6: Forms & Surveys
    {
      element: '#tb_form-builder',
      popover: {
        title: '📝 Forms & Lead Capture',
        description: `
          Forms are how you capture leads. Create forms for:
          • **Opt-ins**: Email signup for newsletter/guide
          • **Contact forms**: "Get a Quote", "Contact Us"
          • **Applications**: Job applications, loan apps, etc.
          • **Surveys**: Multi-question feedback or qualification
          
          Every form submission automatically creates a contact in your database!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 7: Form Builder
    {
      popover: {
        title: '🔧 Build Custom Forms',
        description: `
          The form builder lets you:
          • **Add fields**: Text, email, phone, dropdown, checkboxes, date pickers
          • **Multi-step**: Break long forms into steps for higher completion
          • **Conditional logic**: Show/hide fields based on previous answers
          • **Custom fields**: Map to your contact custom fields
          • **File uploads**: Let people upload resumes, documents, images
          
          From simple name/email to complex applications—all possible!
        `,
        side: 'over',
        align: 'center',
        proTip: 'Keep forms short! Every extra field reduces completion rate. Ask only what you need to start the conversation.'
      }
    },

    // Step 8: Domain Connection
    {
      element: '[href*="domain"], button:contains("Domain")',
      popover: {
        title: '🔗 Connect Your Domain',
        description: `
          Make funnels look professional with your own domain:
          • **Subdomain**: funnel.yourcompany.com (easiest)
          • **Root domain**: yourcompany.com (requires DNS changes)
          • **Free subdomain**: yourname.myhighlevel.link (for testing)
          
          Go to Settings > Domains to add your domain. We'll walk you through DNS setup.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 9: Funnel Steps & Flow
    {
      popover: {
        title: '🔀 Multi-Step Funnels',
        description: `
          Funnels guide visitors through a sequence:
          
          **Typical funnel flow:**
          Landing Page → Opt-in Form → Thank You → Booking Page → Confirmation
          
          Each page has a specific purpose. Visitors flow from one to the next. 
          You can add/remove pages, reorder them, or create branching paths 
          based on choices (e.g., different thank you pages for different options).
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 10: Tracking & Analytics
    {
      element: '[href*="analytic"], [href*="stats"]',
      popover: {
        title: '📊 Track Funnel Performance',
        description: `
          See how your funnels perform:
          • **Visitors**: How many people visited
          • **Opt-ins**: How many submitted forms
          • **Conversion rate**: % who completed the goal
          • **Drop-off points**: Where people leave
          • **Traffic sources**: Where visitors came from
          
          Use this data to optimize. Small tweaks to headlines or buttons can double conversions!
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 11: Embedding Forms
    {
      popover: {
        title: '🔌 Embed Forms Anywhere',
        description: `
          Forms aren't just for funnels—embed them on:
          • **Your existing website** (WordPress, Wix, Squarespace, etc.)
          • **Social media** (Facebook posts, Instagram bio)
          • **Email signatures**
          • **Third-party landing pages**
          
          Just copy the embed code and paste. The form appears seamlessly, 
          and leads still flow into your contact database!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 12: A/B Testing
    {
      popover: {
        title: '🧪 Split Test Your Pages',
        description: `
          Not sure which headline converts better? Test it!
          • Create two versions of a page (A and B)
          • Traffic splits 50/50 between them
          • System tracks which performs better
          • Winner gets more traffic automatically
          
          Test headlines, images, button colors, form length—anything! 
          Data beats opinions every time.
        `,
        side: 'over',
        align: 'center',
        proTip: 'Test one thing at a time. If you change headline AND button color, you won\'t know which made the difference!'
      }
    },

    // Step 13: Publishing & Sharing
    {
      popover: {
        title: '🚀 Publish and Share',
        description: `
          Once your funnel looks great:
          1. Click "Publish" (top right)
          2. Copy the link
          3. Share it:
             • Social media posts
             • Email campaigns
             • Paid ads (Facebook, Google)
             • QR codes for print materials
          
          Every visitor who submits a form becomes a contact automatically. 
          Set up workflows to follow up immediately!
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 14: Completion
    {
      popover: {
        title: '🎉 You\'re a Funnel Builder!',
        description: `
          Amazing! You now know how to:
          ✅ Create funnels from templates
          ✅ Build custom forms for lead capture
          ✅ Use the drag-and-drop builder
          ✅ Connect your own domain
          ✅ Track and optimize performance
          ✅ A/B test to improve conversions
          
          **Next Steps:**
          • Create your first opt-in funnel (use a template!)
          • Connect your domain in Settings
          • Build a simple contact form for your website
          • Set up a workflow to follow up with new leads
          
          Start capturing leads 24/7! 🎯
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Build My Funnel! →'
      }
    }
  ]
};
